package com.bikompanion.ui.composables

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import dev.chrisbanes.haze.HazeInput
import dev.chrisbanes.haze.HazeState
import dev.chrisbanes.haze.glass.hazeGlass
import com.bikompanion.ui.preview.GlassPreviewSurface

private val PillElevation = 2.dp

// The toast's plate: enough to keep small text legible over a busy map.
private const val PillPlateAlpha = 0.6f

/**
 * A small glass plate for status shown over the map, matching the toast and the sheets.
 *
 * Must be a sibling of [hazeState]'s source content, for the reason given on `HomeNavigationBar`.
 */
@Composable
internal fun GlassPill(
    hazeState: HazeState,
    modifier: Modifier = Modifier,
    shape: RoundedCornerShape = CircleShape,
    content: @Composable () -> Unit,
) {
    val glassTint = MaterialTheme.colorScheme.primaryContainer
    val glassStyle = remember(glassTint, shape) { defaultGlassStyle(glassTint, shape) }
    val shadowColor = MaterialTheme.colorScheme.scrim
    Box(
        modifier = modifier
            .shadow(
                elevation = PillElevation,
                shape = shape,
                clip = false,
                ambientColor = shadowColor,
                spotColor = shadowColor,
            ).clip(shape)
            .hazeGlass(input = HazeInput.Sources(hazeState), style = glassStyle)
            .background(MaterialTheme.colorScheme.surfaceContainer.copy(alpha = glassPlateAlpha(PillPlateAlpha))),
    ) {
        CompositionLocalProvider(LocalContentColor provides MaterialTheme.colorScheme.onSurface) {
            content()
        }
    }
}

@Preview
@Composable
private fun GlassPillPreview() {
    GlassPreviewSurface { hazeState ->
        GlassPill(hazeState = hazeState, modifier = Modifier.padding(16.dp)) {
            Text(
                text = "Next update in 24s",
                style = MaterialTheme.typography.labelMedium,
                modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp),
            )
        }
    }
}
